import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  PlusCircle, Loader, Search, Settings,
  MapPin, CheckSquare, Users, MessageCircle, ClipboardCheck, Navigation,
  LayoutDashboard, Receipt, Wallet,
} from "lucide-react";

const adminItems = [
  { to: "/dashboard/home",             label: "Dashboard",       icon: LayoutDashboard, color: "#19e6c8" },
  { to: "/dashboard/create-job",       label: "Create Job",      icon: PlusCircle,     color: "#19e6c8" },
  { to: "/dashboard/approvals",        label: "Approvals",       icon: CheckSquare,    color: "#f59e0b" },
  { to: "/dashboard/job-status",       label: "Job Status",      icon: Loader,         color: "#a78bfa" },
  { to: "/dashboard/job-history",      label: "Job History",     icon: Search,         color: "#3b82f6" },
  { to: "/dashboard/invoicing",        label: "Invoicing",       icon: Receipt,        color: "#ec4899" },
  { to: "/dashboard/financials",       label: "Financials",      icon: Wallet,         color: "#22c55e" },
  { to: "/dashboard/driver-tracking",  label: "Live Tracking",   icon: Navigation,     color: "#19e6c8" },
  { to: "/dashboard/addresses",        label: "Address Book",    icon: MapPin,         color: "#f472b6" },
  { to: "/dashboard/team",             label: "Team",            icon: Users,          color: "#06b6d4" },
  { to: "/dashboard/driver-logs",      label: "Driver Logs",     icon: ClipboardCheck, color: "#f59e0b" },
  { to: "/dashboard/messages",         label: "Messages",        icon: MessageCircle,  color: "#25D366" },
  { to: "/dashboard/settings",         label: "Settings",        icon: Settings,       color: "#9ca3af" },
];

// Allocator — restricted staff role: dispatch/status only, no financials,
// billing, team management, or live tracking.
const allocatorItems = [
  { to: "/dashboard/create-job",  label: "Create Job",   icon: PlusCircle, color: "#19e6c8" },
  { to: "/dashboard/job-status",  label: "Job Status",   icon: Loader,     color: "#a78bfa" },
  { to: "/dashboard/job-history", label: "Job History",  icon: Search,     color: "#3b82f6" },
  { to: "/dashboard/settings",    label: "Settings",     icon: Settings,   color: "#9ca3af" },
];

const clientItems = [
  { to: "/dashboard/new-request",  label: "Create Job",    icon: PlusCircle,      color: "#19e6c8" },
  { to: "/dashboard/job-status",   label: "Job Status",    icon: Loader,          color: "#a78bfa" },
  { to: "/dashboard/job-history",  label: "Job History",   icon: Search,          color: "#3b82f6" },
  { to: "/dashboard/invoicing",    label: "Invoicing",     icon: Receipt,         color: "#ec4899" },
  { to: "/dashboard/addresses",    label: "Address Book",  icon: MapPin,          color: "#ec4899" },
  { to: "/dashboard/my-dashboard", label: "Dashboard",     icon: LayoutDashboard, color: "#19e6c8" },
  { to: "/dashboard/settings",     label: "Settings",      icon: Settings,        color: "#9ca3af" },
];

export default function Sidebar({ onClose, pendingApprovals = 0, overdueInvoices = 0 }) {
  const { user } = useAuth();
  if (!user) return null;
  // Drivers use DriverDashboardLayout instead
  if (user.role === "driver") return null;

  const items = user.role === "admin" ? adminItems
    : user.role === "allocator" ? allocatorItems
    : clientItems;

  // Badge counts, keyed by nav target — Approvals shows pending job requests
  // (admin only), Invoicing shows overdue invoices (admin + client).
  const badges = {
    "/dashboard/approvals": pendingApprovals,
    "/dashboard/invoicing": overdueInvoices,
  };

  return (
    <aside className="df-sidebar w-[260px] py-7 px-4 flex flex-col h-full">
      <div className="px-4 mb-5">
        <p className="brand-title text-white/95 text-sm" data-testid="welcome-label">
          WELCOME, {user.name?.toUpperCase()}!
        </p>
        <p className="text-white/60 text-xs mt-1 uppercase tracking-widest">{user.role}</p>
      </div>

      <nav className="flex flex-col gap-1.5 flex-1">
        {items.map((it) => {
          const Icon = it.icon;
          const badge = badges[it.to] || 0;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={onClose}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
              data-testid={`sidebar-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${it.color}22`, color: it.color }}
              >
                <Icon size={15} />
              </span>
              <span className="flex-1">{it.label}</span>
              {badge > 0 && (
                <span
                  className="min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
                  style={{ background: "#f59e0b", color: "#1a1a2e" }}
                  data-testid={`nav-badge-${it.to.split("/").pop()}`}
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
